import {
  doc,
  getDoc,
  onSnapshot,
  serverTimestamp,
  setDoc,
} from "firebase/firestore";
import { db } from "../services/firebase";
import { logFirebaseAccessError } from "./firebasePermissionDebug";

const STORAGE_KEY = "manucalc.projectBoards.v1";
const BOARD_SYNC_EVENT = "manucalc:project-boards-updated";
const CLOUD_BOARD_DOC_ID = "default";

export const PROJECT_BOARD_SYNC_EVENT = BOARD_SYNC_EVENT;

export const BOARD_PRIORITIES = ["low", "medium", "high", "critical"];
export const BOARD_COLUMN_TYPES = ["standard", "backlog", "active", "blocked", "review", "complete"];
export const BOARD_SORT_OPTIONS = ["position", "priority", "dueDate", "createdAt", "updatedAt", "title", "assignee"];
export const BOARD_DENSITIES = ["compact", "standard", "detailed"];

export const BOARD_TAG_COLORS = [
  "cyan",
  "violet",
  "green",
  "yellow",
  "orange",
  "red",
  "pink",
];

export const BOARD_COLUMN_COLORS = [
  { id: "neutral", label: "Neutral", accent: "#94a3b8" },
  { id: "blue", label: "Planned", accent: "#60a5fa" },
  { id: "cyan", label: "Ready", accent: "#22d3ee" },
  { id: "orange", label: "Active", accent: "#fb923c" },
  { id: "red", label: "Blocked", accent: "#f87171" },
  { id: "violet", label: "Review", accent: "#8b5cf6" },
  { id: "green", label: "Complete", accent: "#4ade80" },
  { id: "yellow", label: "Hold", accent: "#facc15" },
];

export const BOARD_COLUMN_ICONS = [
  { id: "queue", label: "Queue" },
  { id: "ready", label: "Ready" },
  { id: "machine", label: "Machine" },
  { id: "blocked", label: "Blocked" },
  { id: "inspect", label: "Inspect" },
  { id: "ship", label: "Ship" },
  { id: "tool", label: "Tooling" },
  { id: "wrench", label: "Maintenance" },
];

const nowIso = () => new Date().toISOString();

let activeCloudBoardScope = null;
let unsubscribeCloudBoards = null;
let savingCloudBoards = false;

const makeId = (prefix = "item") =>
  `${prefix}_${Date.now().toString(36)}_${Math.random().toString(36).slice(2, 8)}`;

const DEFAULT_COLUMNS = [
  { name: "Backlog", type: "backlog", color: "blue", icon: "queue", description: "Unscheduled or waiting work." },
  { name: "Ready", type: "backlog", color: "cyan", icon: "ready", description: "Released work that can start next." },
  { name: "In Progress", type: "active", color: "orange", icon: "machine", description: "Work currently being done.", workInProgressLimit: 5, workInProgressMode: "advisory" },
  { name: "Review", type: "review", color: "violet", icon: "inspect", description: "Inspection, approval, or customer review." },
  { name: "Done", type: "complete", color: "green", icon: "ship", description: "Finished work ready to ship or close." },
];

export const BOARD_TEMPLATES = [
  {
    id: "launch-readiness",
    name: "Pre-launch readiness",
    description: "Track launch blockers, owner approvals, billing checks, security work, customer experience, and marketing readiness.",
    tags: [
      { name: "Launch blocker", colorToken: "red" },
      { name: "Founder action", colorToken: "yellow" },
      { name: "Security", colorToken: "orange" },
      { name: "Billing", colorToken: "green" },
      { name: "Customer experience", colorToken: "cyan" },
    ],
    cards: [
      { column: "Backlog", title: "Finalize legal policy review", priority: "critical", tags: ["Founder action"], checklist: ["Privacy policy reviewed", "Terms reviewed", "AI/calculation disclaimers reviewed"] },
      { column: "Backlog", title: "Confirm production Stripe products", priority: "critical", tags: ["Billing", "Founder action"], checklist: ["Product IDs verified", "Price IDs verified", "Webhook live mode verified"] },
      { column: "Ready", title: "Review launch pricing page", priority: "high", tags: ["Customer experience"], checklist: ["Lite plan copy", "Pro plan copy", "Business plan copy", "Cancellation copy"] },
      { column: "In Progress", title: "Run production smoke checks", priority: "high", tags: ["Launch blocker"], checklist: ["Build passes", "Tests pass", "Live build info verified"] },
    ],
  },
  {
    id: "manufacturing-job",
    name: "Manufacturing job board",
    description: "Plan a shop job from RFQ through drawing review, programming, setup, production, inspection, and shipment.",
    tags: [
      { name: "RFQ", colorToken: "cyan" },
      { name: "Programming", colorToken: "violet" },
      { name: "Inspection", colorToken: "green" },
      { name: "At risk", colorToken: "red" },
    ],
    cards: [
      { column: "Backlog", title: "Review customer print and notes", priority: "high", tags: ["RFQ"], checklist: ["Material", "Tolerance risk", "Outside process", "Delivery date"] },
      { column: "Ready", title: "Create setup sheet", priority: "medium", tags: ["Programming"], checklist: ["Machine selected", "Tools selected", "Workholding selected"] },
      { column: "Review", title: "Prepare inspection checkpoints", priority: "medium", tags: ["Inspection"], checklist: ["Critical features", "Gage method", "Sampling plan"] },
    ],
  },
];

const createActivity = (message, actor = "ManuCalc", extra = {}) => ({
  id: makeId("activity"),
  message,
  actor,
  createdAt: nowIso(),
  ...extra,
});

const normalizeArray = (value) => (Array.isArray(value) ? value : []);

const timestampValue = (value) => {
  const parsed = new Date(value || 0).getTime();
  return Number.isFinite(parsed) ? parsed : 0;
};

const positionValue = (value, fallback = 0) => {
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : fallback;
};

const positionString = (value, fallback = 0) =>
  String(positionValue(value, fallback)).padStart(6, "0");

const normalizeColor = (value, fallback = "neutral") =>
  BOARD_COLUMN_COLORS.some((color) => color.id === value) ? value : fallback;

const normalizeColumnType = (value, fallback = "standard") =>
  BOARD_COLUMN_TYPES.includes(value) ? value : fallback;

const normalizeSort = (value, fallback = "position") =>
  BOARD_SORT_OPTIONS.includes(value) ? value : fallback;

const normalizeDensity = (value, fallback = "standard") =>
  BOARD_DENSITIES.includes(value) ? value : fallback;

const defaultPermissions = () => ({
  editBoardStructure: ["owner", "admin"],
  createCards: ["owner", "admin", "employee"],
  editAllCards: ["owner", "admin"],
  editAssignedCards: ["owner", "admin", "employee"],
  assignUsers: ["owner", "admin"],
  archiveCards: ["owner", "admin"],
  deleteCards: ["owner"],
  manageTemplates: ["owner", "admin"],
  viewAnalytics: ["owner", "admin"],
});

const normalizeBoard = (board = {}, scope = getBoardOwnerScope(), index = 0) => ({
  id: board.id || makeId("board"),
  ownerType: board.ownerType || scope.ownerType,
  ownerId: board.ownerId || scope.ownerId,
  workspaceId: board.workspaceId || (scope.ownerType === "workspace" ? scope.ownerId : null),
  projectId: board.projectId || null,
  name: board.name || `Project board ${index + 1}`,
  description: board.description || "",
  visibility: board.visibility || (scope.ownerType === "workspace" ? "workspace" : "private"),
  cardDensity: normalizeDensity(board.cardDensity),
  defaultView: board.defaultView || "board",
  permissions: { ...defaultPermissions(), ...(board.permissions || {}) },
  customFields: normalizeArray(board.customFields).map((field, fieldIndex) => ({
    id: field.id || makeId("field"),
    name: field.name || `Custom field ${fieldIndex + 1}`,
    type: field.type || "text",
    options: normalizeArray(field.options),
    position: positionValue(field.position, fieldIndex),
    showOnCard: field.showOnCard !== false,
    archivedAt: field.archivedAt || null,
  })),
  createdBy: board.createdBy || "ManuCalc",
  createdAt: board.createdAt || nowIso(),
  updatedAt: board.updatedAt || board.createdAt || nowIso(),
  archivedAt: board.archivedAt || null,
});

const normalizeColumn = (column = {}, index = 0) => {
  const inferredType = column.name === "Done" ? "complete" : "standard";
  const type = normalizeColumnType(column.type, inferredType);
  const colorToken = normalizeColor(column.colorToken || column.color, type === "complete" ? "green" : type === "blocked" ? "red" : "neutral");
  return {
    id: column.id || makeId("column"),
    boardId: column.boardId,
    workspaceId: column.workspaceId || null,
    projectId: column.projectId || null,
    name: column.name || `Column ${index + 1}`,
    description: column.description || "",
    type,
    colorToken,
    color: colorToken,
    icon: column.icon || null,
    position: positionString(column.position, index),
    workInProgressLimit: column.workInProgressLimit ?? column.cardLimit ?? null,
    workInProgressMode: column.workInProgressMode || "advisory",
    defaultSort: normalizeSort(column.defaultSort || column.sort || "position"),
    restrictedMoveInRoles: normalizeArray(column.restrictedMoveInRoles),
    restrictedMoveOutRoles: normalizeArray(column.restrictedMoveOutRoles),
    isCompletionColumn: Boolean(column.isCompletionColumn || type === "complete"),
    isBlockedColumn: Boolean(column.isBlockedColumn || type === "blocked"),
    isReviewColumn: Boolean(column.isReviewColumn || type === "review"),
    collapsedByUser: column.collapsedByUser || {},
    createdBy: column.createdBy || "ManuCalc",
    createdAt: column.createdAt || nowIso(),
    updatedAt: column.updatedAt || column.createdAt || nowIso(),
    archivedAt: column.archivedAt || null,
  };
};

const normalizeCard = (card = {}, index = 0) => ({
  id: card.id || makeId("card"),
  boardId: card.boardId,
  columnId: card.columnId,
  projectId: card.projectId || null,
  manufacturingPlanId: card.manufacturingPlanId || null,
  title: card.title || "Untitled card",
  description: card.description || "",
  position: positionString(card.position, index),
  priority: BOARD_PRIORITIES.includes(card.priority) ? card.priority : "medium",
  assigneeIds: normalizeArray(card.assigneeIds),
  taskAssignees: card.taskAssignees || {},
  tagIds: normalizeArray(card.tagIds),
  customFieldValues: card.customFieldValues || {},
  startDate: card.startDate || null,
  dueDate: card.dueDate || null,
  checklist: normalizeArray(card.checklist).map((item) =>
    typeof item === "string"
      ? { id: makeId("check"), title: item, complete: false, assigneeIds: [] }
      : { ...item, assigneeIds: normalizeArray(item.assigneeIds) }
  ),
  comments: normalizeArray(card.comments),
  attachments: normalizeArray(card.attachments),
  attachmentCount: Number(card.attachmentCount ?? normalizeArray(card.attachments).length),
  commentCount: Number(card.commentCount ?? normalizeArray(card.comments).length),
  blocked: Boolean(card.blocked),
  blockedReason: card.blockedReason || "",
  createdBy: card.createdBy || "ManuCalc",
  updatedBy: card.updatedBy || card.createdBy || "ManuCalc",
  createdAt: card.createdAt || nowIso(),
  updatedAt: card.updatedAt || card.createdAt || nowIso(),
  completedAt: card.completedAt || null,
  archivedAt: card.archivedAt || null,
});

const normalizeBoardData = (data = {}, scope = getBoardOwnerScope()) => ({
  boards: normalizeArray(data.boards).map((board, index) => normalizeBoard(board, scope, index)),
  columns: normalizeArray(data.columns).map((column, index) => normalizeColumn(column, index)),
  cards: normalizeArray(data.cards).map((card, index) => normalizeCard(card, index)),
  tags: normalizeArray(data.tags).map((tag) => ({
    id: tag.id || makeId("tag"),
    boardId: tag.boardId,
    name: tag.name || "Label",
    colorToken: BOARD_TAG_COLORS.includes(tag.colorToken) ? tag.colorToken : "cyan",
  })),
  activity: normalizeArray(data.activity),
  boardTemplates: normalizeArray(data.boardTemplates),
});

export const getBoardOwnerScope = ({ user, accountProfile } = {}) => {
  const workspaceId =
    accountProfile?.activeWorkspaceId ||
    accountProfile?.companyId ||
    accountProfile?.companyOwnerUid ||
    "";

  if (workspaceId) {
    return {
      ownerType: "workspace",
      ownerId: String(workspaceId),
      storageKey: `workspace:${workspaceId}`,
    };
  }

  const uid = user?.uid || "anonymous";
  return {
    ownerType: "user",
    ownerId: String(uid),
    storageKey: `user:${uid}`,
  };
};

export const getBoardRole = ({ user, accountProfile } = {}) => {
  if (!user?.uid) return "collaborator";
  const role = String(accountProfile?.role || accountProfile?.workspaceRole || accountProfile?.companyRole || "").toLowerCase();
  const accountType = String(accountProfile?.accountType || accountProfile?.planAccountType || "").toLowerCase();
  const ownerIds = [
    accountProfile?.ownerUid,
    accountProfile?.companyOwnerUid,
    accountProfile?.businessOwnerUid,
    accountProfile?.createdByUid,
  ].filter(Boolean).map(String);
  if (
    role === "owner" ||
    role === "businessowner" ||
    role === "business_owner" ||
    role === "companyowner" ||
    role === "company_owner" ||
    accountProfile?.adminDashboardEnabled ||
    ownerIds.includes(String(user.uid)) ||
    (accountType === "business" && !role)
  ) return "owner";
  if (["admin", "administrator", "manager", "businessadmin", "business_admin", "companyadmin", "company_admin"].includes(role)) return "admin";
  if (["employee", "member", "operator"].includes(role)) return "employee";
  return accountProfile?.companyId || accountProfile?.companyOwnerUid ? "employee" : "owner";
};

export const getBoardPermissions = ({ user, accountProfile, board } = {}) => {
  const role = getBoardRole({ user, accountProfile });
  const permissions = { ...defaultPermissions(), ...(board?.permissions || {}) };
  const allowed = (key) => normalizeArray(permissions[key]).includes(role);
  return {
    role,
    canEditBoardStructure: allowed("editBoardStructure"),
    canCreateCards: allowed("createCards"),
    canEditAllCards: allowed("editAllCards"),
    canEditAssignedCards: allowed("editAssignedCards"),
    canAssignUsers: allowed("assignUsers"),
    canArchiveCards: allowed("archiveCards"),
    canDeleteCards: allowed("deleteCards"),
    canManageTemplates: allowed("manageTemplates"),
    canViewAnalytics: allowed("viewAnalytics"),
  };
};

const readAllBoards = () => {
  if (typeof window === "undefined") return {};
  try {
    return JSON.parse(window.localStorage.getItem(STORAGE_KEY) || "{}");
  } catch {
    return {};
  }
};

const writeAllBoards = (data) => {
  if (typeof window === "undefined") return;
  window.localStorage.setItem(STORAGE_KEY, JSON.stringify(data));
  window.dispatchEvent(new CustomEvent(BOARD_SYNC_EVENT));
};

const getCloudBoardPath = (user, accountProfile = {}) => {
  if (!user?.uid) return null;
  const workspaceId =
    accountProfile?.activeWorkspaceId ||
    accountProfile?.companyId ||
    accountProfile?.companyOwnerUid ||
    "";
  if (workspaceId) {
    return ["companies", String(workspaceId), "projectBoards", CLOUD_BOARD_DOC_ID];
  }
  return ["users", user.uid, "projectBoards", CLOUD_BOARD_DOC_ID];
};

const mergeByUpdatedAt = (localItems = [], cloudItems = []) => {
  const merged = new Map();
  [...normalizeArray(localItems), ...normalizeArray(cloudItems)].forEach((item) => {
    if (!item?.id) return;
    const current = merged.get(item.id);
    if (!current || timestampValue(item.updatedAt || item.createdAt) >= timestampValue(current.updatedAt || current.createdAt)) {
      merged.set(item.id, item);
    }
  });
  return [...merged.values()];
};

const mergeActivity = (localItems = [], cloudItems = []) => {
  const merged = new Map();
  [...normalizeArray(localItems), ...normalizeArray(cloudItems)].forEach((item) => {
    if (!item?.id) return;
    merged.set(item.id, item);
  });
  return [...merged.values()]
    .sort((a, b) => timestampValue(b.createdAt) - timestampValue(a.createdAt))
    .slice(0, 500);
};

const mergeBoardData = (localData = {}, cloudData = {}) => ({
  boards: mergeByUpdatedAt(localData.boards, cloudData.boards),
  columns: mergeByUpdatedAt(localData.columns, cloudData.columns),
  cards: mergeByUpdatedAt(localData.cards, cloudData.cards),
  tags: mergeByUpdatedAt(localData.tags, cloudData.tags),
  boardTemplates: mergeByUpdatedAt(localData.boardTemplates, cloudData.boardTemplates),
  activity: mergeActivity(localData.activity, cloudData.activity),
});

const sanitizeBoardDataForCloud = (data = {}) => ({
  boards: normalizeArray(data.boards),
  columns: normalizeArray(data.columns),
  cards: normalizeArray(data.cards).map((card) => ({
    ...card,
    attachments: normalizeArray(card.attachments).map((attachment) => ({
      id: attachment.id,
      name: attachment.name || "Attachment",
      size: Number(attachment.size || 0),
      type: attachment.type || "file",
      addedAt: attachment.addedAt || nowIso(),
      storagePath: attachment.storagePath || "",
      downloadUrl: attachment.downloadUrl || "",
    })),
    attachmentCount: normalizeArray(card.attachments).length,
  })),
  tags: normalizeArray(data.tags),
  boardTemplates: normalizeArray(data.boardTemplates),
  activity: normalizeArray(data.activity).slice(0, 500),
});

const saveProjectBoardsToCloud = (scope, data) => {
  if (
    !activeCloudBoardScope ||
    !activeCloudBoardScope.path ||
    activeCloudBoardScope.storageKey !== scope?.storageKey ||
    savingCloudBoards
  ) {
    return;
  }

  const payload = sanitizeBoardDataForCloud(data);
  setDoc(
    doc(db, ...activeCloudBoardScope.path),
    {
      ...payload,
      id: CLOUD_BOARD_DOC_ID,
      ownerId: activeCloudBoardScope.ownerId,
      companyId: activeCloudBoardScope.companyId,
      workspaceScope: activeCloudBoardScope.companyId ? "company" : "user",
      updatedAt: nowIso(),
      updatedAtServer: serverTimestamp(),
      updatedBy: activeCloudBoardScope.uid,
    },
    { merge: true }
  ).catch((error) => {
    logFirebaseAccessError({
      error,
      path: activeCloudBoardScope.path.join("/"),
      operation: "create/update",
      user: { uid: activeCloudBoardScope.uid },
      companyId: activeCloudBoardScope.companyId,
      context: "saveProjectBoardsToCloud",
    });
  });
};

const sortByPosition = (items = []) =>
  [...items].sort((a, b) => positionValue(a.position) - positionValue(b.position));

const canUsePermission = (options, key) =>
  !options?.permissions || Boolean(options.permissions[key]);

const canEditCardWithPermission = (card = {}, options = {}) => {
  if (!options?.permissions) return true;
  if (options.permissions.canEditAllCards) return true;
  const uid = options.userId || "";
  return Boolean(options.permissions.canEditAssignedCards && uid && normalizeArray(card.assigneeIds).includes(uid));
};

const denyBoardMutation = (data, message, actor = "ManuCalc", extra = {}) => ({
  ...data,
  activity: [createActivity(message, actor, { ...extra, denied: true }), ...normalizeArray(data.activity)],
});

export const createBoardFromTemplate = ({
  templateId = "launch-readiness",
  scope = getBoardOwnerScope(),
  createdBy = "ManuCalc",
  name,
} = {}) => {
  const template = BOARD_TEMPLATES.find((item) => item.id === templateId) || BOARD_TEMPLATES[0];
  const boardId = makeId("board");
  const createdAt = nowIso();
  const columns = DEFAULT_COLUMNS.map((column, index) => ({
    id: makeId("column"),
    boardId,
    workspaceId: scope.ownerType === "workspace" ? scope.ownerId : null,
    projectId: null,
    name: column.name,
    description: column.description || "",
    type: column.type || "standard",
    position: positionString(index),
    color: column.color,
    colorToken: column.color,
    icon: column.icon || null,
    cardLimit: column.workInProgressLimit || null,
    workInProgressLimit: column.workInProgressLimit || null,
    workInProgressMode: column.workInProgressMode || "advisory",
    defaultSort: "position",
    restrictedMoveInRoles: [],
    restrictedMoveOutRoles: [],
    isCompletionColumn: column.type === "complete",
    isBlockedColumn: column.type === "blocked",
    isReviewColumn: column.type === "review",
    collapsedByUser: {},
    createdAt,
    updatedAt: createdAt,
  }));
  const tags = template.tags.map((tag) => ({
    id: makeId("tag"),
    boardId,
    name: tag.name,
    colorToken: tag.colorToken,
  }));
  const cards = template.cards.map((card, index) => {
    const column = columns.find((item) => item.name === card.column) || columns[0];
    const cardTags = tags.filter((tag) => normalizeArray(card.tags).includes(tag.name)).map((tag) => tag.id);
    return {
      id: makeId("card"),
      boardId,
      columnId: column.id,
      position: positionString(index),
      title: card.title,
      description: card.description || "",
      priority: card.priority || "medium",
      dueDate: null,
      assigneeIds: [],
      tagIds: cardTags,
      customFieldValues: {},
      checklist: normalizeArray(card.checklist).map((title) => ({
        id: makeId("check"),
        title,
        complete: false,
        assigneeIds: [],
      })),
      comments: [],
      attachments: [],
      attachmentCount: 0,
      commentCount: 0,
      createdBy,
      createdAt,
      updatedAt: createdAt,
      completedAt: null,
      archivedAt: null,
    };
  });

  return {
    board: {
      id: boardId,
      ownerType: scope.ownerType,
      ownerId: scope.ownerId,
      workspaceId: scope.ownerType === "workspace" ? scope.ownerId : null,
      projectId: null,
      name: name || template.name,
      description: template.description,
      visibility: scope.ownerType === "workspace" ? "workspace" : "private",
      cardDensity: "standard",
      defaultView: "board",
      permissions: defaultPermissions(),
      customFields: normalizeArray(template.customFields).map((field, index) => ({
        id: makeId("field"),
        position: index,
        type: "text",
        options: [],
        showOnCard: true,
        ...field,
      })),
      createdBy,
      createdAt,
      updatedAt: createdAt,
      archivedAt: null,
    },
    columns,
    cards,
    tags,
    activity: [createActivity(`Created board from ${template.name} template`, createdBy)],
  };
};

export const loadProjectBoards = (scope = getBoardOwnerScope()) => {
  const allBoards = readAllBoards();
  const scopeData = allBoards[scope.storageKey];
  if (scopeData?.boards?.length) return normalizeBoardData(scopeData, scope);

  return {
    boards: [],
    columns: [],
    cards: [],
    tags: [],
    boardTemplates: [],
    activity: [],
  };
};

export const saveProjectBoards = (scope = getBoardOwnerScope(), data) => {
  const allBoards = readAllBoards();
  allBoards[scope.storageKey] = normalizeBoardData(data, scope);
  writeAllBoards(allBoards);
  saveProjectBoardsToCloud(scope, allBoards[scope.storageKey]);
  return allBoards[scope.storageKey];
};

export const mergeCloudProjectBoards = (scope = getBoardOwnerScope(), cloudData = {}) => {
  const localData = loadProjectBoards(scope);
  const merged = mergeBoardData(localData, cloudData);
  const allBoards = readAllBoards();
  allBoards[scope.storageKey] = merged;
  writeAllBoards(allBoards);
  return merged;
};

export const stopProjectBoardCloudSync = () => {
  unsubscribeCloudBoards?.();
  unsubscribeCloudBoards = null;
  activeCloudBoardScope = null;
};

export const startProjectBoardCloudSync = async ({ user, accountProfile } = {}) => {
  stopProjectBoardCloudSync();
  if (!user?.uid) return () => {};

  const path = getCloudBoardPath(user, accountProfile);
  if (!path) return () => {};

  const scope = getBoardOwnerScope({ user, accountProfile });
  activeCloudBoardScope = {
    ...scope,
    path,
    uid: user.uid,
    ownerId: user.uid,
    companyId:
      accountProfile?.activeWorkspaceId ||
      accountProfile?.companyId ||
      accountProfile?.companyOwnerUid ||
      "",
  };

  const boardRef = doc(db, ...path);
  try {
    const snapshot = await getDoc(boardRef);
    if (snapshot.exists()) {
      const merged = mergeCloudProjectBoards(scope, snapshot.data());
      savingCloudBoards = true;
      try {
        await setDoc(boardRef, {
          ...sanitizeBoardDataForCloud(merged),
          id: CLOUD_BOARD_DOC_ID,
          ownerId: activeCloudBoardScope.ownerId,
          companyId: activeCloudBoardScope.companyId,
          workspaceScope: activeCloudBoardScope.companyId ? "company" : "user",
          updatedAt: nowIso(),
          updatedAtServer: serverTimestamp(),
          updatedBy: activeCloudBoardScope.uid,
        }, { merge: true });
      } finally {
        savingCloudBoards = false;
      }
    } else {
      saveProjectBoardsToCloud(scope, loadProjectBoards(scope));
    }
  } catch (error) {
    logFirebaseAccessError({
      error,
      path: path.join("/"),
      operation: "read",
      user,
      role: accountProfile?.role,
      companyId: activeCloudBoardScope.companyId,
      context: "startProjectBoardCloudSync initial load",
    });
  }

  unsubscribeCloudBoards = onSnapshot(
    boardRef,
    (snapshot) => {
      if (!snapshot.exists()) return;
      savingCloudBoards = true;
      try {
        mergeCloudProjectBoards(scope, snapshot.data());
      } finally {
        savingCloudBoards = false;
      }
    },
    (error) => {
      logFirebaseAccessError({
        error,
        path: path.join("/"),
        operation: "listen",
        user,
        role: accountProfile?.role,
        companyId: activeCloudBoardScope?.companyId,
        context: "projectBoards snapshot",
      });
    }
  );

  return stopProjectBoardCloudSync;
};

export const ensureDefaultProjectBoard = ({ scope, createdBy } = {}) => {
  const activeScope = scope || getBoardOwnerScope();
  const data = loadProjectBoards(activeScope);
  if (data.boards.length) return data;
  const templateBoard = createBoardFromTemplate({ scope: activeScope, createdBy });
  return saveProjectBoards(activeScope, {
    boards: [templateBoard.board],
    columns: templateBoard.columns,
    cards: templateBoard.cards,
    tags: templateBoard.tags,
    activity: templateBoard.activity,
  });
};

const updateBoardData = (scope, updater) => {
  const current = loadProjectBoards(scope);
  const next = updater(current);
  return saveProjectBoards(scope, next);
};

export const createBoard = (scope, { templateId, name, createdBy }, options = {}) =>
  updateBoardData(scope, (data) => {
    if (!canUsePermission(options, "canEditBoardStructure")) {
      return denyBoardMutation(data, "Board creation requires board structure permission.", createdBy);
    }
    const created = createBoardFromTemplate({ templateId, name, scope, createdBy });
    return {
      ...data,
      boards: [...data.boards, created.board],
      columns: [...data.columns, ...created.columns],
      cards: [...data.cards, ...created.cards],
      tags: [...data.tags, ...created.tags],
      activity: [...created.activity, ...data.activity],
    };
  });

export const updateBoardSettings = (scope, boardId, updates = {}, actor = "ManuCalc", options = {}) =>
  updateBoardData(scope, (data) => {
    if (!canUsePermission(options, "canEditBoardStructure")) {
      return denyBoardMutation(data, "Board settings require board structure permission.", actor, { boardId });
    }
    return {
      ...data,
      boards: data.boards.map((board) =>
        board.id === boardId ? normalizeBoard({ ...board, ...updates, updatedAt: nowIso() }, scope) : board
      ),
      activity: [createActivity("Updated board settings", actor, { boardId }), ...data.activity],
    };
  });

export const addBoardColumn = (scope, boardId, values = {}, actor = "ManuCalc", afterColumnId = "", options = {}) =>
  updateBoardData(scope, (data) => {
    if (!canUsePermission(options, "canEditBoardStructure")) {
      return denyBoardMutation(data, "Column creation requires board structure permission.", actor, { boardId });
    }
    const boardColumns = sortByPosition(data.columns.filter((column) => column.boardId === boardId && !column.archivedAt));
    const afterIndex = afterColumnId ? boardColumns.findIndex((column) => column.id === afterColumnId) : boardColumns.length - 1;
    const insertIndex = Math.max(0, afterIndex + 1);
    const createdAt = nowIso();
    const column = normalizeColumn({
      ...values,
      id: makeId("column"),
      boardId,
      position: positionString(insertIndex),
      createdBy: actor,
      createdAt,
      updatedAt: createdAt,
    }, insertIndex);
    const reordered = [
      ...boardColumns.slice(0, insertIndex),
      column,
      ...boardColumns.slice(insertIndex),
    ].map((item, index) => ({ ...item, position: positionString(index), updatedAt: item.id === column.id ? item.updatedAt : nowIso() }));
    const otherColumns = data.columns.filter((item) => item.boardId !== boardId || item.archivedAt);
    return {
      ...data,
      columns: [...otherColumns, ...reordered],
      activity: [createActivity(`Added column "${column.name}"`, actor, { boardId, columnId: column.id }), ...data.activity],
    };
  });

export const updateBoardColumn = (scope, columnId, updates = {}, actor = "ManuCalc", options = {}) =>
  updateBoardData(scope, (data) => {
    if (!canUsePermission(options, "canEditBoardStructure")) {
      return denyBoardMutation(data, "Column updates require board structure permission.", actor, { columnId });
    }
    return {
      ...data,
      columns: data.columns.map((column) =>
        column.id === columnId ? normalizeColumn({ ...column, ...updates, updatedAt: nowIso() }) : column
      ),
      activity: [createActivity("Updated column", actor, { columnId }), ...data.activity],
    };
  });

export const duplicateBoardColumn = (scope, columnId, actor = "ManuCalc", options = {}) =>
  updateBoardData(scope, (data) => {
    if (!canUsePermission(options, "canEditBoardStructure")) {
      return denyBoardMutation(data, "Column duplication requires board structure permission.", actor, { columnId });
    }
    const source = data.columns.find((column) => column.id === columnId);
    if (!source) return data;
    const created = normalizeColumn({
      ...source,
      id: makeId("column"),
      name: `${source.name} Copy`,
      position: positionString(positionValue(source.position) + 0.5),
      createdBy: actor,
      createdAt: nowIso(),
      updatedAt: nowIso(),
      archivedAt: null,
    });
    return {
      ...data,
      columns: sortByPosition([...data.columns, created]).map((column, index) => ({ ...column, position: positionString(index) })),
      activity: [createActivity(`Duplicated column "${source.name}"`, actor, { columnId: created.id }), ...data.activity],
    };
  });

export const archiveBoardColumn = (scope, columnId, actor = "ManuCalc", { archiveCards = false, moveCardsToColumnId = "" } = {}, options = {}) =>
  updateBoardData(scope, (data) => {
    if (!canUsePermission(options, "canEditBoardStructure")) {
      return denyBoardMutation(data, "Column archive requires board structure permission.", actor, { columnId });
    }
    const column = data.columns.find((item) => item.id === columnId);
    if (!column) return data;
    const activeCards = data.cards.filter((card) => card.columnId === columnId && !card.archivedAt);
    if (activeCards.length && !archiveCards && !moveCardsToColumnId) return data;
    return {
      ...data,
      columns: data.columns.map((item) =>
        item.id === columnId ? { ...item, archivedAt: nowIso(), updatedAt: nowIso() } : item
      ),
      cards: data.cards.map((card) => {
        if (card.columnId !== columnId || card.archivedAt) return card;
        if (archiveCards) return { ...card, archivedAt: nowIso(), updatedAt: nowIso() };
        return { ...card, columnId: moveCardsToColumnId, updatedAt: nowIso() };
      }),
      activity: [createActivity(`Archived column "${column.name}"`, actor, { columnId }), ...data.activity],
    };
  });

export const restoreBoardColumn = (scope, columnId, actor = "ManuCalc", options = {}) =>
  updateBoardData(scope, (data) => {
    if (!canUsePermission(options, "canEditBoardStructure")) {
      return denyBoardMutation(data, "Column restore requires board structure permission.", actor, { columnId });
    }
    const restored = data.columns.find((column) => column.id === columnId);
    const activeColumns = sortByPosition(data.columns.filter((column) => column.boardId === restored?.boardId && !column.archivedAt));
    return {
      ...data,
      columns: data.columns.map((column) =>
        column.id === columnId ? { ...column, archivedAt: null, position: positionString(activeColumns.length), updatedAt: nowIso() } : column
      ),
      activity: [createActivity("Restored column", actor, { columnId }), ...data.activity],
    };
  });

export const deleteBoardColumn = (scope, columnId, actor = "ManuCalc", options = {}) =>
  updateBoardData(scope, (data) => {
    if (!canUsePermission(options, "canEditBoardStructure")) {
      return denyBoardMutation(data, "Column deletion requires board structure permission.", actor, { columnId });
    }
    if (data.cards.some((card) => card.columnId === columnId)) {
      return denyBoardMutation(data, "Only columns with no active or archived cards can be deleted.", actor, { columnId });
    }
    const deleted = data.columns.find((column) => column.id === columnId);
    const remaining = data.columns.filter((column) => column.id !== columnId);
    const reordered = remaining.map((column) => {
      if (column.boardId !== deleted?.boardId || column.archivedAt) return column;
      const position = sortByPosition(remaining.filter((item) => item.boardId === deleted?.boardId && !item.archivedAt)).findIndex((item) => item.id === column.id);
      return { ...column, position: positionString(Math.max(0, position)), updatedAt: nowIso() };
    });
    return {
      ...data,
      columns: reordered,
      activity: [createActivity("Deleted empty column", actor, { columnId }), ...data.activity],
    };
  });

export const toggleBoardColumnCollapse = (scope, columnId, userId = "local", actor = "ManuCalc") =>
  updateBoardData(scope, (data) => ({
    ...data,
    columns: data.columns.map((column) =>
      column.id === columnId
        ? {
            ...column,
            collapsedByUser: {
              ...(column.collapsedByUser || {}),
              [userId]: !column.collapsedByUser?.[userId],
            },
            updatedAt: nowIso(),
          }
        : column
    ),
    activity: [createActivity("Changed personal column collapse state", actor, { columnId }), ...data.activity],
  }));

export const addBoardCard = (scope, boardId, columnId, values = {}, options = {}) =>
  updateBoardData(scope, (data) => {
    if (!canUsePermission(options, "canCreateCards")) {
      return denyBoardMutation(data, "Card creation requires board card permission.", values.createdBy || "ManuCalc", { boardId, columnId });
    }
    const createdAt = nowIso();
    const columnCards = data.cards.filter((card) => card.columnId === columnId);
    const card = {
      id: makeId("card"),
      boardId,
      columnId,
      position: positionString(columnCards.length),
      title: values.title || "New card",
      description: values.description || "",
      priority: values.priority || "medium",
      dueDate: values.dueDate || null,
      assigneeIds: normalizeArray(values.assigneeIds),
      tagIds: normalizeArray(values.tagIds),
      customFieldValues: values.customFieldValues || {},
      checklist: [],
      comments: [],
      attachments: [],
      attachmentCount: 0,
      commentCount: 0,
      createdBy: values.createdBy || "ManuCalc",
      createdAt,
      updatedAt: createdAt,
      completedAt: null,
      archivedAt: null,
    };
    return {
      ...data,
      cards: [...data.cards, card],
      activity: [createActivity(`Added card "${card.title}"`, card.createdBy, { cardId: card.id }), ...data.activity],
    };
  });

export const updateBoardCard = (scope, cardId, updates = {}, actor = "ManuCalc", options = {}) =>
  updateBoardData(scope, (data) => {
    const source = data.cards.find((card) => card.id === cardId);
    if (!canEditCardWithPermission(source, options)) {
      return denyBoardMutation(data, "Card update requires assignment or board-wide card permission.", actor, { cardId });
    }
    return {
      ...data,
      cards: data.cards.map((card) =>
        card.id === cardId
          ? normalizeCard({
              ...card,
              ...updates,
              tagIds: updates.tagIds ? normalizeArray(updates.tagIds) : card.tagIds,
              assigneeIds: updates.assigneeIds ? normalizeArray(updates.assigneeIds) : card.assigneeIds,
              updatedBy: actor,
              updatedAt: nowIso(),
            })
          : card
      ),
      activity: [createActivity(`Updated card`, actor, { cardId }), ...data.activity],
    };
  });

export const checkColumnMovePolicy = (data, cardId, toColumnId, role = "owner") => {
  const card = normalizeArray(data.cards).find((item) => item.id === cardId);
  const column = normalizeArray(data.columns).find((item) => item.id === toColumnId);
  if (!card || !column) return { allowed: false, warning: "Card or destination column was not found." };
  if (normalizeArray(column.restrictedMoveInRoles).length && !column.restrictedMoveInRoles.includes(role)) {
    return { allowed: false, warning: "Your role cannot move cards into this column." };
  }
  const activeCards = normalizeArray(data.cards).filter((item) => item.columnId === toColumnId && item.id !== cardId && !item.archivedAt);
  const limit = Number(column.workInProgressLimit || 0);
  if (limit > 0 && activeCards.length >= limit) {
    return {
      allowed: column.workInProgressMode !== "enforced",
      warning: `${column.name} is at its WIP limit (${activeCards.length}/${limit}).`,
    };
  }
  return { allowed: true, warning: "" };
};

export const moveBoardCard = (scope, cardId, toColumnId, toPosition = 0, actor = "ManuCalc", options = {}) =>
  updateBoardData(scope, (data) => {
    const moving = data.cards.find((card) => card.id === cardId);
    if (!moving) return data;
    if (!canEditCardWithPermission(moving, options)) {
      return denyBoardMutation(data, "Card move requires assignment or board-wide card permission.", actor, { cardId, toColumnId });
    }
    const policy = checkColumnMovePolicy(data, cardId, toColumnId, options.role || "owner");
    if (!policy.allowed && !options.override) {
      return {
        ...data,
        activity: [createActivity(policy.warning, actor, { cardId, toColumnId, blockedMove: true }), ...data.activity],
      };
    }

    const otherCards = data.cards.filter((card) => card.id !== cardId);
    const targetCards = sortByPosition(otherCards.filter((card) => card.columnId === toColumnId));
    const nextPosition = Math.max(0, Math.min(Number(toPosition) || 0, targetCards.length));
    const updatedMoving = { ...moving, columnId: toColumnId, position: positionString(nextPosition), updatedBy: actor, updatedAt: nowIso() };
    const reorderedTarget = [
      ...targetCards.slice(0, nextPosition),
      updatedMoving,
      ...targetCards.slice(nextPosition),
    ].map((card, index) => ({ ...card, position: positionString(index) }));
    const untouched = otherCards.filter((card) => card.columnId !== toColumnId);
    const sourceReordered = sortByPosition(untouched.filter((card) => card.columnId === moving.columnId))
      .map((card, index) => ({ ...card, position: positionString(index) }));
    const finalUntouched = untouched.filter((card) => card.columnId !== moving.columnId);

    return {
      ...data,
      cards: [...finalUntouched, ...sourceReordered, ...reorderedTarget],
      activity: [createActivity(`Moved card "${moving.title}"`, actor, { cardId, toColumnId, warning: policy.warning }), ...data.activity],
    };
  });

export const moveBoardColumn = (scope, columnId, direction = 0, actor = "ManuCalc", options = {}) =>
  updateBoardData(scope, (data) => {
    if (!canUsePermission(options, "canEditBoardStructure")) {
      return denyBoardMutation(data, "Column reorder requires board structure permission.", actor, { columnId });
    }
    const movingColumn = data.columns.find((column) => column.id === columnId);
    if (!movingColumn) return data;
    const columns = sortByPosition(data.columns.filter((column) => column.boardId === movingColumn.boardId && !column.archivedAt));
    const otherColumns = data.columns.filter((column) => column.boardId !== movingColumn.boardId || column.archivedAt);
    const fromIndex = columns.findIndex((column) => column.id === columnId);
    const toIndex = fromIndex + Number(direction);
    if (fromIndex < 0 || toIndex < 0 || toIndex >= columns.length) return data;
    const [column] = columns.splice(fromIndex, 1);
    columns.splice(toIndex, 0, column);
    return {
      ...data,
      columns: [...otherColumns, ...columns.map((item, index) => ({ ...item, position: positionString(index), updatedAt: nowIso() }))],
      activity: [createActivity(`Reordered column "${column.name}"`, actor, { columnId }), ...data.activity],
    };
  });

export const addCardComment = (scope, cardId, body, actor = "ManuCalc", options = {}) =>
  updateBoardData(scope, (data) => {
    const source = data.cards.find((card) => card.id === cardId);
    if (!canEditCardWithPermission(source, options)) {
      return denyBoardMutation(data, "Card comments require assignment or board-wide card permission.", actor, { cardId });
    }
    return {
      ...data,
      cards: data.cards.map((card) => {
        if (card.id !== cardId) return card;
        const comments = [
          ...normalizeArray(card.comments),
          { id: makeId("comment"), body, actor, createdAt: nowIso() },
        ];
        return { ...card, comments, commentCount: comments.length, updatedAt: nowIso() };
      }),
      activity: [createActivity("Added card comment", actor, { cardId }), ...data.activity],
    };
  });

export const addCardAttachment = (scope, cardId, attachment, actor = "ManuCalc", options = {}) =>
  updateBoardData(scope, (data) => {
    const source = data.cards.find((card) => card.id === cardId);
    if (!canEditCardWithPermission(source, options)) {
      return denyBoardMutation(data, "Card attachments require assignment or board-wide card permission.", actor, { cardId });
    }
    return {
      ...data,
      cards: data.cards.map((card) => {
        if (card.id !== cardId) return card;
        const attachments = [
          ...normalizeArray(card.attachments),
          {
            id: makeId("attachment"),
            name: attachment.name,
            size: attachment.size || 0,
            type: attachment.type || "file",
            addedAt: nowIso(),
          },
        ];
        return { ...card, attachments, attachmentCount: attachments.length, updatedAt: nowIso() };
      }),
      activity: [createActivity("Added card attachment", actor, { cardId }), ...data.activity],
    };
  });

export const toggleChecklistItem = (scope, cardId, itemId, actor = "ManuCalc", options = {}) =>
  updateBoardData(scope, (data) => {
    const source = data.cards.find((card) => card.id === cardId);
    if (!canEditCardWithPermission(source, options)) {
      return denyBoardMutation(data, "Checklist updates require assignment or board-wide card permission.", actor, { cardId });
    }
    return {
      ...data,
      cards: data.cards.map((card) =>
        card.id === cardId
          ? {
              ...card,
              checklist: normalizeArray(card.checklist).map((item) =>
                item.id === itemId ? { ...item, complete: !item.complete } : item
              ),
              updatedAt: nowIso(),
            }
          : card
      ),
      activity: [createActivity("Updated checklist", actor, { cardId }), ...data.activity],
    };
  });

export const addChecklistItem = (scope, cardId, title, actor = "ManuCalc", options = {}) =>
  updateBoardData(scope, (data) => {
    const source = data.cards.find((card) => card.id === cardId);
    if (!canEditCardWithPermission(source, options)) {
      return denyBoardMutation(data, "Checklist updates require assignment or board-wide card permission.", actor, { cardId });
    }
    return {
      ...data,
      cards: data.cards.map((card) =>
        card.id === cardId
          ? {
              ...card,
              checklist: [...normalizeArray(card.checklist), { id: makeId("check"), title, complete: false }],
              updatedAt: nowIso(),
            }
          : card
      ),
      activity: [createActivity("Added checklist item", actor, { cardId }), ...data.activity],
    };
  });

export const archiveBoardCard = (scope, cardId, actor = "ManuCalc", options = {}) => {
  if (options?.permissions && !options.permissions.canArchiveCards) {
    return updateBoardData(scope, (data) => denyBoardMutation(data, "Card archive requires archive permission.", actor, { cardId }));
  }
  return updateBoardCard(scope, cardId, { archivedAt: nowIso() }, actor, options);
};

export const restoreBoardCard = (scope, cardId, actor = "ManuCalc", options = {}) =>
  updateBoardCard(scope, cardId, { archivedAt: null }, actor, options);

export const saveBoardAsTemplate = (scope, boardId, name, actor = "ManuCalc", options = {}) =>
  updateBoardData(scope, (data) => {
    if (!canUsePermission(options, "canManageTemplates")) {
      return denyBoardMutation(data, "Template creation requires template management permission.", actor, { boardId });
    }
    const view = getBoardViewModel(data, boardId);
    if (!view.board) return data;
    const template = {
      id: makeId("template"),
      name: name || `${view.board.name} template`,
      description: view.board.description || "Custom board template",
      createdBy: actor,
      createdAt: nowIso(),
      columns: view.allColumns.map(({ id, boardId: _boardId, ...column }) => ({ ...column })),
      customFields: normalizeArray(view.board.customFields),
      tags: view.tags.map(({ id, boardId: _boardId, ...tag }) => ({ ...tag })),
    };
    return {
      ...data,
      boardTemplates: [...normalizeArray(data.boardTemplates), template],
      activity: [createActivity(`Saved board template "${template.name}"`, actor, { boardId }), ...data.activity],
    };
  });

export const getBoardViewModel = (data, boardId, options = {}) => {
  const normalized = normalizeBoardData(data);
  const board = normalized.boards.find((item) => item.id === boardId) || normalized.boards[0] || null;
  const allColumns = sortByPosition(normalized.columns.filter((column) => column.boardId === board?.id));
  const boardColumns = allColumns.filter((column) => !column.archivedAt);
  const boardCards = sortByPosition(normalized.cards.filter((card) => card.boardId === board?.id));
  const boardTags = normalized.tags.filter((tag) => tag.boardId === board?.id);
  const userId = options.userId || "local";
  return {
    board,
    columns: boardColumns.map((column) => ({
      ...column,
      collapsed: Boolean(column.collapsedByUser?.[userId]),
      cards: boardCards.filter((card) => card.columnId === column.id && !card.archivedAt),
      workInProgressStatus: column.workInProgressLimit
        ? {
            count: boardCards.filter((card) => card.columnId === column.id && !card.archivedAt).length,
            limit: Number(column.workInProgressLimit),
            mode: column.workInProgressMode || "advisory",
          }
        : null,
    })),
    allColumns,
    archivedColumns: allColumns.filter((column) => column.archivedAt),
    archivedCards: boardCards.filter((card) => card.archivedAt),
    tags: boardTags,
    activity: normalizeArray(normalized.activity).filter((item) => !item.boardId || item.boardId === board?.id),
    boardTemplates: normalizeArray(normalized.boardTemplates),
  };
};

export const filterBoardCards = (cards = [], { search = "", priority = "", tagId = "", sort = "position", assignee = "", overdue = false, blocked = false, unassigned = false } = {}) => {
  const query = String(search || "").trim().toLowerCase();
  const filtered = cards.filter((card) => {
    const matchesSearch =
      !query ||
      [card.title, card.description, card.priority, ...normalizeArray(card.assigneeIds), ...Object.values(card.customFieldValues || {})]
        .filter(Boolean)
        .join(" ")
        .toLowerCase()
        .includes(query);
    const matchesPriority = !priority || card.priority === priority;
    const matchesTag = !tagId || normalizeArray(card.tagIds).includes(tagId);
    const matchesAssignee = !assignee || normalizeArray(card.assigneeIds).includes(assignee);
    const today = new Date().toISOString().slice(0, 10);
    const matchesOverdue = !overdue || (card.dueDate && card.dueDate < today && !card.completedAt);
    const matchesBlocked = !blocked || card.blocked;
    const matchesUnassigned = !unassigned || !normalizeArray(card.assigneeIds).length;
    return matchesSearch && matchesPriority && matchesTag && matchesAssignee && matchesOverdue && matchesBlocked && matchesUnassigned;
  });

  if (sort === "dueDate") {
    return filtered.sort((a, b) => String(a.dueDate || "9999").localeCompare(String(b.dueDate || "9999")));
  }
  if (sort === "priority") {
    const order = { critical: 0, high: 1, medium: 2, low: 3 };
    return filtered.sort((a, b) => (order[a.priority] ?? 4) - (order[b.priority] ?? 4));
  }
  if (sort === "createdAt" || sort === "updatedAt") {
    return filtered.sort((a, b) => timestampValue(b[sort]) - timestampValue(a[sort]));
  }
  if (sort === "title") {
    return filtered.sort((a, b) => String(a.title || "").localeCompare(String(b.title || "")));
  }
  if (sort === "assignee") {
    return filtered.sort((a, b) => String(a.assigneeIds?.[0] || "~").localeCompare(String(b.assigneeIds?.[0] || "~")));
  }
  return sortByPosition(filtered);
};
